import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

/**
 * PermissionsContext — Phase 4 foundation.
 *
 * Fetches the current user's RBAC permissions once after login (from
 * `GET /api/rbac/users/{user_id}/permissions`) and exposes:
 *   - permissions: string[]  (e.g. ["tasks.view", "tasks.create", ...])
 *   - rbacRoleName: string | null
 *   - can(permissionName): boolean
 *   - hasAny([...]): boolean
 *   - hasAll([...]): boolean
 *   - loaded: boolean  (guard for flicker-free UI)
 *   - refresh(): Promise<void>
 *
 * Notes:
 *   - Manager / Admin are treated as having every permission — matches the
 *     legacy `role_level` behaviour so we can safely replace scattered
 *     `role_level === 'Manager'` checks without regressions.
 *   - On logout or token change, permissions are cleared and refetched.
 *   - Network failure is non-blocking: `can()` returns `false` and the UI
 *     falls back to whatever legacy check was already present.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PermissionsContext = createContext(null);

export const usePermissions = () => {
  const ctx = useContext(PermissionsContext);
  if (!ctx) {
    throw new Error('usePermissions must be used within PermissionsProvider');
  }
  return ctx;
};

export const PermissionsProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [permissions, setPermissions] = useState([]);
  const [rbacRoleName, setRbacRoleName] = useState(null);
  const [rbacRoleDisplayName, setRbacRoleDisplayName] = useState(null);
  const [pageAccess, setPageAccess] = useState({});
  const [loaded, setLoaded] = useState(false);
  const lastFetchKey = useRef(null);

  const fetchPermissions = useCallback(
    async (force = false) => {
      if (!token || !user?.id) {
        setPermissions([]);
        setRbacRoleName(null);
        setRbacRoleDisplayName(null);
        setPageAccess({});
        setLoaded(true);
        return;
      }
      const key = `${user.id}:${token}`;
      if (!force && lastFetchKey.current === key) return;
      try {
        const res = await axios.get(
          `${API}/rbac/users/${user.id}/permissions`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = res.data || {};
        const raw = Array.isArray(data.permissions) ? data.permissions : [];
        const names = raw
          .map((p) => (typeof p === 'string' ? p : p?.name))
          .filter(Boolean);
        setPermissions(names);
        setRbacRoleName(data.role_name || null);
        setRbacRoleDisplayName(data.role_display_name || null);
        setPageAccess(data.page_access || {});
        lastFetchKey.current = key;
      } catch (e) {
        console.warn('Permissions fetch failed (non-blocking):', e?.message);
        setPermissions([]);
        setRbacRoleName(null);
        setRbacRoleDisplayName(null);
        setPageAccess({});
      } finally {
        setLoaded(true);
      }
    },
    [token, user?.id]
  );

  useEffect(() => {
    setLoaded(false);
    fetchPermissions();
  }, [fetchPermissions]);

  const permSet = useMemo(() => new Set(permissions), [permissions]);

  // Phase 5 — admin override is now driven exclusively by the RBAC role.
  // Legacy `role_level === 'Manager'` is no longer trusted; the migration
  // (`migrate_user_roles()` on backend startup) ensures every Manager has
  // `rbac_role_id` mapped to the `admin` role, so behaviour is unchanged
  // in steady state but the source of truth is unified.
  const isAdminRole = rbacRoleName === 'admin';

  const can = useCallback(
    (permName) => {
      if (!permName) return false;
      if (isAdminRole) return true;
      // Module wildcard: caller can ask `can("tasks.*")`
      if (permName.endsWith('.*')) {
        const prefix = permName.slice(0, -1); // "tasks."
        for (const p of permSet) {
          if (p.startsWith(prefix)) return true;
        }
        return false;
      }
      return permSet.has(permName);
    },
    [permSet, isAdminRole]
  );

  const hasAny = useCallback(
    (list) => Array.isArray(list) && list.some((p) => can(p)),
    [can]
  );

  const hasAll = useCallback(
    (list) => Array.isArray(list) && list.every((p) => can(p)),
    [can]
  );

  const value = useMemo(
    () => ({
      permissions,
      rbacRoleName,
      rbacRoleDisplayName,
      pageAccess,
      hasPageAccess: (key) => {
        if (!key) return false;
        if (isAdminRole) return true;
        return pageAccess?.[key] === true;
      },
      loaded,
      can,
      hasAny,
      hasAll,
      refresh: () => fetchPermissions(true),

      // Identity flags (driven by the RBAC role name, not legacy `role_level`).
      isAdminRole,
      isManager: isAdminRole,

      // Convenience permission helpers — replace the legacy `useAuth()`
      // helpers. Always return a boolean.
      canDeleteTasks: () => can('tasks.delete'),
      canModifyTasks: () => hasAny(['tasks.edit', 'tasks.create']),
      canApproveTasks: () => can('tasks.approve'),
      canAssignTasks: () => can('tasks.assign'),

      canDeleteSocialMedia: () => can('social_media.delete'),
      canModifySocialMedia: () => hasAny(['social_media.edit', 'social_media.create']),

      canManageUsers: () => can('settings.manage_users'),
      canViewAllUsers: () => can('settings.view'),
    }),
    [permissions, rbacRoleName, rbacRoleDisplayName, pageAccess, loaded, can, hasAny, hasAll, fetchPermissions, isAdminRole]
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

/**
 * Declarative gate component.
 *   <Can permission="tasks.delete">...</Can>
 *   <Can anyOf={["tasks.edit", "tasks.create"]}>...</Can>
 * Hides (returns null) when the check fails.
 * Accepts a `fallback` prop if a placeholder is preferred.
 */
export const Can = ({ permission, anyOf, allOf, fallback = null, children }) => {
  const { can, hasAny, hasAll } = usePermissions();
  let allowed = false;
  if (permission) allowed = can(permission);
  else if (Array.isArray(anyOf)) allowed = hasAny(anyOf);
  else if (Array.isArray(allOf)) allowed = hasAll(allOf);
  return allowed ? <>{children}</> : fallback;
};

export default PermissionsProvider;
